import { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import DeliveryTicket from '../../components/print/DeliveryTicket';
import { BASE_URL } from "../../apiConfig";
import { useAuth } from "../../context/AuthProvider";
import Loading from '../../cors/Loading';
import { useParams } from "react-router-dom";
import { 
  HandCoins, 
  Truck, 
  Clock, 
  Package, 
  XCircle, 
  CheckCircle, 
  ChevronLeft, 
  ChevronRight,
  RefreshCcw, Search, 
  Loader2, 
  Send,
  AlertCircle,
  ChevronDown,
} from 'lucide-react';
import OrderList from '../../elements/Order_list'; 
import { toast } from "sonner";
import { statuses } from "../../cors/statusMap";
import FilterOrders from "../../cors/FilterOrders";
import LivreurSelectionModal from '../../components/modals/LivreurSelectionModal';

export default function OrdersDashboard() {
  const { token, role } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { status } = useParams();
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [sendingOrders, setSendingOrders] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState();
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [selectedLivreur, setSelectedLivreur] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [showFailedOrders, setShowFailedOrders] = useState(false);
  const [showLivreurModal, setShowLivreurModal] = useState(false);
  const [ordersToPrint, setOrdersToPrint] = useState([]);
  const ticketRef = useRef();

  const handlePrint = useReactToPrint({
    contentRef: ticketRef,
    documentTitle: 'Tickets de Livraison',
    onAfterPrint: () => setOrdersToPrint([])
  });

  useEffect(() => {
    if (ordersToPrint.length > 0) {
      handlePrint();
    }
  }, [ordersToPrint]);
  const [state, setState] = useState({
    total_revenue: 0,
    total_orders: 0,
    orders_today: 0,
    orders_en_cours: 0
  });
  const [sendProgress, setSendProgress] = useState({
    isVisible: false,
    current: 0,
    total: 0,
    percentage: 0,
    status: 'idle',
    failedCount: 0,
    successCount: 0,
    notSentOrders: []
  });

  useEffect(() => {
    fetchOrders()
  }, [status]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 20,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0
  });

  const handleSelectOrder = (orderId) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  function formatDateFR(dateString) {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }).replace(/\//g, '/');
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pagination.currentPage]);

  const fetchOrders = async (page = 1, search = searchTerm, range = dateRange, sellerId = null, livreurId = null) => {
    try {
      const requestBody = { 
        status: status, 
        userRole: role, 
        search: search 
      };

      console.log('Range received in fetchOrders:', range);

      if (range && range.start_date && range.end_date) {
        requestBody.range = {
          start_date: range.start_date,
          end_date: range.end_date
        };
      }

      if (sellerId) {
        requestBody.seller_id = sellerId;
      }

      if (livreurId) {
        requestBody.livreur_id = livreurId;
      }

      console.log('Final request body:', requestBody);

      const response = await fetch(
        `${BASE_URL}/api/getorders?page=${page}&per_page=${pagination.perPage}`,
        {
          method: 'POST',
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify(requestBody)
        }
      );
      const data = await response.json();
      
      if (data.code === 200) {
        setOrders(data.orders);
        setState(data.stats);
        setPagination({
          currentPage: data.pagination.current_page,
          perPage: data.pagination.per_page,
          total: data.pagination.total,
          lastPage: data.pagination.last_page,
          from: data.pagination.from,
          to: data.pagination.to
        });
      }
      setLoading(false);        
    } catch (error) {
      console.error("Error fetching orders:", error);
      setLoading(false);
    }
  };

  const handleAssignToLivreur = async (livreurId) => {
    if (selectedOrders.length < 1) {
      toast.warning('Aucune commande sélectionnée');
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/assign-orders-to-livreur`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ 
          order_ids: selectedOrders, 
          livreur_id: livreurId 
        }),
      });

      const data = await response.json();

      if (data.code === 200) {
        toast.success(`${data.assigned_count} commande(s) assignée(s) au livreur avec succès`);
        
        // Filter the full order objects to print
        const selectedFullOrders = orders.filter(o => selectedOrders.includes(o.id));
        setOrdersToPrint(selectedFullOrders);

        fetchOrders(pagination.currentPage);
        setSelectedOrders([]);
        setShowLivreurModal(false);
      } else {
        toast.error(data.message || 'Erreur lors de l\'assignation des commandes');
      }
    } catch (error) {
      console.error("Error assigning orders to livreur:", error);
      toast.error('Erreur réseau lors de l\'assignation des commandes');
    }
  };

  const handleSendOrders = async () => {
    if (selectedOrders.length < 1) {
      toast.warning('Aucune commande sélectionnée');
      return;
    }

    setSendProgress({
      isVisible: true,
      current: 0,
      total: selectedOrders.length,
      percentage: 0,
      status: 'preparing',
      failedCount: 0,
      successCount: 0,
      notSentOrders: []
    });

    setSendingOrders(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 800));

      setSendProgress(prev => ({
        ...prev,
        status: 'sending',
        current: 0,
        percentage: 5
      }));           

      const response = await fetch(`${BASE_URL}/api/send-delivrey-orders`, {
        method: 'POST',
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({ orders: selectedOrders, company: selectedCompany }),
      });

      const data = await response.json();

      if (data.code === 200) {
        const notSentOrders = data.not_sent_orders || [];
        const successCount = selectedOrders.length - notSentOrders.length;

        setSendProgress(prev => ({
          ...prev,
          current: selectedOrders.length,
          percentage: 100,
          status: 'completed',
          successCount: successCount,
          failedCount: notSentOrders.length,
          notSentOrders: notSentOrders
        }));


        if (notSentOrders.length === 0) {
          toast.success(`Toutes les commandes ont été envoyées avec succès (${selectedOrders.length} commandes)`);
        } else {
          toast.warning(`${successCount} commande(s) envoyée(s) avec succès, ${notSentOrders.length} échouée(s)`);
        }

        fetchOrders(pagination.currentPage);
        setSelectedOrders([]);
      } else {
        setSendProgress(prev => ({
          ...prev,
          status: 'error'
        }));
        toast.error(data.message || 'Erreur lors de l\'envoi des commandes');
      }
    } catch (error) {
      console.error("Error sending orders:", error);
      setSendProgress(prev => ({ ...prev, status: 'error' }));
      toast.error('Erreur réseau lors de l\'envoi des commandes');
    } finally {
      setTimeout(() => {
        setSendingOrders(false);
      }, 1200);
    }
  };

  const getStatusColor = (status) => statuses[status]?.color || "bg-gray-100 text-gray-800 border-gray-200";
  const getStatusText = (status) => statuses[status]?.text || status;
  const getStatusIcon = (status, iconSize = 14) => {
    const Icon = statuses[status]?.icon;
    return Icon ? <Icon size={iconSize} className="mr-1" /> : null;
  };

  const paginate = (pageNumber) => fetchOrders(pageNumber, searchTerm, dateRange, selectedSeller?.value, selectedLivreur?.value);
  const nextPage = () => paginate(pagination.currentPage + 1);
  const prevPage = () => paginate(pagination.currentPage - 1);

  const getProgressStatusText = () => {
    switch (sendProgress.status) {
      case 'preparing':
        return 'Préparation des commandes...';
      case 'sending':
        return 'Envoi en cours...';
      case 'completed':
        return 'Envoi terminé !';
      case 'error':
        return 'Erreur lors de l\'envoi';
      default:
        return 'Traitement...';
    }
  };

  function formatCount(amount) {
    return new Intl.NumberFormat('en-US', {
    }).format(amount || 0);
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  }

  return (
    <div className="bg-gray-50">
      {/* Progress Modal */}
      {sendProgress.isVisible && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[99] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl  w-full max-w-md">
            <div className="overflow-y-auto max-h-96 my-6 pl-6 pr-4 mr-2 custom-scrollbar">
              <div className="flex items-center justify-center mb-4">
                <div className={`p-3 rounded-full ${
                  sendProgress.status === 'completed' 
                    ? sendProgress.failedCount > 0 ? 'bg-yellow-100' : 'bg-green-100'
                    : sendProgress.status === 'error' ? 'bg-red-100' : 'bg-blue-100'
                }`}>
                  {sendProgress.status === 'completed' ? (
                    sendProgress.failedCount > 0 ? (
                      <AlertCircle className="w-8 h-8 text-yellow-600" />
                    ) : (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    )
                  ) : sendProgress.status === 'error' ? (
                    <XCircle className="w-8 h-8 text-red-600" />
                  ) : (
                    <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                  )}
                </div>
              </div>
              
              <h3 className="text-lg font-semibold text-center text-gray-900 mb-2">
                {getProgressStatusText()}
              </h3>
              
              <p className="text-sm text-gray-600 text-center mb-4">
                {sendProgress.status === 'completed' 
                  ? sendProgress.failedCount > 0
                    ? `${sendProgress.successCount} envoyée(s), ${sendProgress.failedCount} échouée(s)`
                    : `${sendProgress.total} commandes envoyées avec succès`
                  : sendProgress.status === 'error'
                  ? 'Une erreur est survenue lors de l\'envoi'
                  : `Traitement de ${sendProgress.total} commandes...`
                }
              </p>

              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-4 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-500 ease-out ${
                    sendProgress.status === 'completed' 
                      ? sendProgress.failedCount > 0 ? 'bg-yellow-500' : 'bg-green-500'
                      : sendProgress.status === 'error' ? 'bg-red-500' :
                      'bg-[var(--color-primary-500)]'
                  }`}
                  style={{ width: `${sendProgress.percentage}%` }}
                />
              </div>

              {/* Progress Text */}
              <div className="flex justify-between text-xs text-gray-500 mb-2">
                <span>Progression</span>
                <span>{sendProgress.percentage}%</span>
              </div>

              {/* Count */}
              <div className="text-center text-xs text-gray-600 mb-4">
                {sendProgress.current} / {sendProgress.total} commandes traitées
              </div>

              {/* Failed Orders Section */}
              {sendProgress.failedCount > 0 && sendProgress.status === 'completed' && (
                <div className="mb-4 border-t pt-4">
                  <button
                    onClick={() => setShowFailedOrders(!showFailedOrders)}
                    className="w-full flex items-center justify-between p-2 rounded-lg bg-red-50 hover:bg-red-100 transition-colors"
                  >
                    <div className="flex cursor-pointer items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-xs font-medium text-red-600">
                        {sendProgress.failedCount} commande(s) non envoyée(s)
                      </span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-red-600 transition-transform ${showFailedOrders ? 'rotate-180' : ''}`} />
                  </button>

                  {showFailedOrders && (
                    <div className="mt-2 max-h-48 overflow-y-auto bg-red-50 rounded-lg p-3">
                      {sendProgress.notSentOrders.map((failedOrder, idx) => (
                        <div key={idx} className="mb-2 pb-2 border-b border-red-200 last:border-b-0">
                          <div className="flex items-start gap-2">
                            <div className="mt-0.5">
                              <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-red-900">
                                Commande ID: {failedOrder.order_id}
                              </p>
                              <p className="text-xs text-red-700 mt-0.5 break-words">
                                {failedOrder.reason || 'Raison inconnue'}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Close button for completed/error states */}
              {(sendProgress.status === 'completed' || sendProgress.status === 'error') && (
                <button
                  onClick={() => setSendProgress(prev => ({ ...prev, isVisible: false }))}
                  className="w-full cursor-pointer mt-4 bg-[var(--color-primary-500)] text-xs text-white py-2 rounded-lg hover:bg-[var(--color-primary-400)] transition-colors"
                >
                  Fermer
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Livreur Selection Modal */}
      <LivreurSelectionModal
        isOpen={showLivreurModal}
        onClose={() => setShowLivreurModal(false)}
        selectedOrders={selectedOrders}
        onAssign={handleAssignToLivreur}
      />

      {/* Hidden Print Component */}
      <div style={{ display: 'none' }}>
        <DeliveryTicket ref={ticketRef} orders={ordersToPrint} />
      </div>

      <div className="">
        <div className="max-w-7xl mx-auto px-4 pt-12 pb-12 max-lg:pb-5">
          <div className="flex items-center justify-between max-sm:grid max-sm:justify-normal">
            <div className="">
              {
                status === "tout" ?
                <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
                  Touts les Commandes 
                </h1>
                :
                <h1 className="text-4xl max-md:text-2xl font-bold bg-gradient-to-r from-[var(--color-primary-500)] to-[var(--color-primary-400)] bg-clip-text text-transparent mb-4">
                  Les Commandes {getStatusText(status)}
                </h1>
              }
              <p className="text-sm max-xl:text-xs text-gray-600 max-w-2xl">
                Gérez et suivez vos commandes
              </p>
            </div>
            <FilterOrders
              role={role}
              token={token}
              status={status}
              loading={loading}
              setLoading={setLoading}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              fetchOrders={fetchOrders}
              setDateRange={setDateRange}
              selectedOrders={selectedOrders}
              handleSendOrders={handleSendOrders}
              sendingOrders={sendingOrders}
              selectedSeller={selectedSeller}
              setSelectedSeller={setSelectedSeller}
              selectedLivreur={selectedLivreur}
              setSelectedLivreur={setSelectedLivreur}
              selectedCompany={selectedCompany}
              setSelectedCompany={setSelectedCompany}
              setShowLivreurModal={setShowLivreurModal}
              pagination={pagination} />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm py-6 px-5 border border-gray-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <HandCoins className="text-teal-600" />
              </div>
              <div className="ml-2">
                <p className="text-sm font-medium text-gray-600">Revenu Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatMoney(state.total_revenue) || 0 } MAD
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Package className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Commandes Total</p>
                <p className="text-2xl font-bold text-gray-900">{formatCount(state.total_orders) || 0 }</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Commandes d'Aujourd'hui</p>
                <p className="text-2xl font-bold text-gray-900">{formatCount(state.orders_today) || 0 }</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100">
                <Truck className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">En livraison</p>
                <p className="text-2xl font-bold text-gray-900">{formatCount(state.orders_en_cours) || 0 }</p>
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        {loading ? (
          <div className="mt-40 bg-gray-50 flex items-center justify-center">
            <Loading />
          </div>
        ) : (
          <div className="bg-white rounded-xl mb-5 shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-2 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">Liste des Commandes</h2>
              <p className="text-xs text-gray-600 mt-1">
                Affichage {pagination.from} à {pagination.to} sur {pagination.total} commandes
              </p>
              {selectedOrders.length > 0 && (
                <p className="text-xs text-[var(--color-primary-500)] font-medium mt-1">
                  {selectedOrders.length} commande(s) sélectionnée(s)
                </p>
              )}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr className="">
                    {
                      status === "confirme" ?
                        <th className="pt-2">
                          <div className="inline-flex items-center">
                            <label
                              className="relative flex cursor-pointer items-center rounded-full"
                              data-ripple-dark="true"
                            >
                              <input
                                type="checkbox"
                                checked={selectedOrders.length === orders.length && orders.length > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedOrders(orders.map(order => order.id));
                                  } else {
                                    setSelectedOrders([]);
                                  }
                                }}
                                className="before:content[''] peer border-gray-300 relative h-5 w-5 cursor-pointer appearance-none rounded-md border border-blue-gray-200 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-teal-500 checked:bg-teal-500 checked:before:bg-teal-500 hover:before:opacity-10"
                              />
                              <div className="pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-white opacity-0 transition-opacity peer-checked:opacity-100">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-3.5 w-3.5"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  stroke="currentColor"
                                  strokeWidth="1"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                    clipRule="evenodd"
                                  ></path>
                                </svg>
                              </div>
                            </label>
                          </div>
                        </th>
                      :''
                    }
                    <th className={`${status === "confirme" ? 'pr-2' : 'pl-6 pr-2'}  py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider`}>
                      Code
                    </th>
                    <th className="px-2 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Affiliate
                    </th>
                    <th className="px-2 py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="py-3 text-left text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Produits
                    </th>
                    <th className="px-2 py-3 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-2 py-3 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Total
                    </th>
                    <th className="px-2 py-3 text-center text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Bénéfice
                    </th>
                    <th className="pl-5 py-3 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <OrderList
                      key={order.id}
                      order={order}
                      formatDateFR={formatDateFR}
                      getStatusColor={getStatusColor}
                      getStatusText={getStatusText}
                      getStatusIcon={getStatusIcon}
                      fetchOrders={fetchOrders}
                      page={pagination.currentPage}
                      status={status}
                      selected={selectedOrders.includes(order.id)}
                      onSelect={handleSelectOrder}
                      role={role}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {orders.length === 0 && (
              <div className="text-center py-12">
                <Package className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande trouvée</h3>
                <p className="mt-1 text-sm text-gray-500">Aucune commande ne correspond au filtre sélectionné.</p>
              </div>
            )}

            {/* Improved Pagination */}
            {orders.length > 0 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    Affichage <span className="font-medium">{pagination.from}</span> à{" "}
                    <span className="font-medium">{pagination.to}</span> sur{" "}
                    <span className="font-medium">{pagination.total}</span> résultats
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={prevPage}
                      disabled={pagination.currentPage === 1}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === 1 ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pagination.lastPage) }, (_, i) => {
                        let pageNum;
                        if (pagination.lastPage <= 5) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (pagination.currentPage >= pagination.lastPage - 2) {
                          pageNum = pagination.lastPage - 4 + i;
                        } else {
                          pageNum = pagination.currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => paginate(pageNum)}
                            className={`px-3 py-1 cursor-pointer text-sm rounded-md ${pagination.currentPage === pageNum ? 'bg-[var(--color-primary-500)] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <span className="px-2 text-gray-500">...</span>
                      )}
                      
                      {pagination.lastPage > 5 && pagination.currentPage < pagination.lastPage - 2 && (
                        <button
                          onClick={() => paginate(pagination.lastPage)}
                          className={`px-3 py-1 cursor-pointer text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50`}
                        >
                          {pagination.lastPage}
                        </button>
                      )}
                    </div>
                    
                    <button
                      onClick={nextPage}
                      disabled={pagination.currentPage === pagination.lastPage}
                      className={`px-1 py-1 cursor-pointer rounded-md border ${pagination.currentPage === pagination.lastPage ? 'border-gray-200 text-gray-400 cursor-not-allowed' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}